import { useEffect, useState } from "react";
import { useNotification } from "context/NotificationContext";
import { useNavigate } from "react-router-dom";

import {
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Menu,
  MenuItem,
  Modal,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import MDBadge from "components/MDBadge";
import taskService from "../../../services/taskService";
import { DateFormat } from "../../../services/common";
import MDButton from "components/MDButton";

const ListTask = (props) => {
  const { refreshTasks, onEdit } = props;
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [menu, setMenu] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tasksDone, setTasksDone] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAllTasks() {
      setLoading(true);
      try {
        const [pendingData, doneData] = await Promise.all([
          taskService.getAllTasks(),
          taskService.getAllTasksDone(),
        ]);
        setTasks(Array.isArray(pendingData) ? pendingData : []);
        setTasksDone(Array.isArray(doneData) ? doneData : []);
      } catch {
        showNotification("Error al cargar las tareas", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchAllTasks();
  }, [refreshTasks, refresh]);

  const changeState = async (isChecked, id) => {
    const state = isChecked ? "DONE" : "PENDING";
    const response = await taskService.updateTaskStateById({ id, state });
    if (response.success) {
      showNotification(response.message, "success");
      setRefresh((prev) => !prev);
    } else {
      showNotification(response.message, "error");
    }
  };

  const openMenu = (currentTarget, id) => {
    setMenu(currentTarget);
    setTaskId(id);
  };

  const closeMenu = () => {
    setMenu(null);
    setTaskId(null);
  };

  const handleEdit = async (id) => {
    closeMenu();
    const task = tasks.find((t) => t.id === id);
    onEdit(task);
  };

  const handleDelete = async (id) => {
    closeMenu();
    const shouldDelete = window.confirm("¿Realmente desea eliminar este registro?");
    if (shouldDelete) {
      const response = await taskService.deleteTaskById(id);
      if (response.success) {
        showNotification(response.message, "success");
        setRefresh((prev) => !prev);
      } else {
        showNotification(response.message, "error");
      }
    }
  };

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={() => handleView(taskId)}>Ver</MenuItem>
      <MenuItem onClick={() => handleEdit(taskId)}>Editar</MenuItem>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={() => handleDelete(taskId)}>Eliminar</MenuItem>
    </Menu>
  );

  const handleView = (id) => {
    const selectedTask = tasks.find((t) => t.id === id);
    closeMenu();
    setSelectedEvent(selectedTask);
    setOpenModal(true);
  };
  const handleClose = () => {
    setOpenModal(false);
    setSelectedEvent(null);
  };

  const renderModal = (
    <Modal open={openModal} onClose={handleClose}>
      <MDBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: (theme) => theme.palette.background.default,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          minWidth: 300,
        }}
      >
        {selectedEvent && (
          <>
            <MDBox px={2} textAlign="center" lineHeight={1.25}>
              <MDBox pb={2} mx={3} mt={-3} display="flex" justifyContent="center">
                <MDBox
                  display="grid"
                  justifyContent="center"
                  alignItems="center"
                  bgColor={
                    selectedEvent.finish_date && Date.parse(selectedEvent.finish_date) > Date.now()
                      ? "success"
                      : "info"
                  }
                  color="white"
                  width="4rem"
                  height="4rem"
                  shadow="md"
                  borderRadius="lg"
                  variant="gradient"
                >
                  <NotificationsIcon fontSize="default" />
                </MDBox>
              </MDBox>
              <MDTypography pt={2} display="block" variant="button" fontWeight="medium">
                EXPEDIENTE
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                style={{ whiteSpace: "pre-line" }}
              >
                {selectedEvent.expediente_id ? selectedEvent.caratula : " - "}
              </MDTypography>
              <MDTypography pt={2} display="block" variant="button" fontWeight="medium">
                TÍTULO
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                style={{ whiteSpace: "pre-line" }}
              >
                {selectedEvent.title}
              </MDTypography>
              <MDTypography pt={2} display="block" variant="button" fontWeight="medium">
                DESCRIPCIÓN
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                style={{ whiteSpace: "pre-line" }}
              >
                {selectedEvent.description ? selectedEvent.description : " - "}
              </MDTypography>
              <MDTypography pt={2} display="block" variant="button" fontWeight="medium">
                FECHA DE VENCIMIENTO
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                style={{ whiteSpace: "pre-line" }}
              >
                {selectedEvent.finish_date ? DateFormat(selectedEvent.finish_date) : " - "}
              </MDTypography>

              <Divider />
              {selectedEvent.expediente_id && (
                <MDButton
                  color="info"
                  onClick={() => navigate("/file/detail/" + selectedEvent.expediente_id)}
                >
                  Ver expediente
                </MDButton>
              )}
              <MDBox pt={1}>
                <MDButton variant="text" color="secondary" onClick={handleClose}>
                  Cerrar
                </MDButton>
              </MDBox>
            </MDBox>
          </>
        )}
      </MDBox>
    </Modal>
  );

  const listTasks = tasks.map((x) => (
    <Grid key={x.id} item xs={12} md={12} lg={12}>
      <Card>
        <CardContent>
          <MDBox component="li" display="flex" alignItems="center" mb={-2}>
            <Checkbox
              sx={{ pl: 0 }}
              checked={x.state === "DONE"}
              onChange={(e) => changeState(e.target.checked, x.id)}
            />
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <MDTypography variant="button" fontWeight="medium">
                {x.title}
              </MDTypography>
              {x.description && (
                <MDTypography variant="button" fontWeight="medium" color="text">
                  {x.description}
                </MDTypography>
              )}
            </MDBox>
            <MDBox ml="auto">
              {x.finish_date && (
                <MDBadge
                  badgeContent={x.finish_date && DateFormat(x.finish_date)}
                  color={new Date(x.finish_date) < new Date() ? "error" : "success"}
                  container
                ></MDBadge>
              )}
            </MDBox>
            <Icon
              sx={{ cursor: "pointer", fontWeight: "bold", ml: 1 }}
              fontSize="small"
              onClick={(e) => openMenu(e.currentTarget, x.id)}
            >
              more_vert
            </Icon>
          </MDBox>
        </CardContent>
      </Card>
    </Grid>
  ));

  const listTasksDone = tasksDone.map((x) => (
    <Grid key={x.id} item xs={12} md={12} lg={12}>
      <Card
        sx={{
          opacity: x.state === "DONE" ? 0.5 : 1,
        }}
      >
        <CardContent>
          <MDBox component="li" display="flex" alignItems="center" mb={-2}>
            <Checkbox
              sx={{ pl: 0 }}
              checked={x.state === "DONE"}
              onChange={(e) => changeState(e.target.checked, x.id)}
            />
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <MDTypography variant="button" fontWeight="medium">
                {x.title ? x.title : null}
              </MDTypography>
              {x.description && (
                <MDTypography variant="button" fontWeight="medium" color="text">
                  {x.description}
                </MDTypography>
              )}
              {x.caratula && (
                <MDTypography variant="button" fontWeight="medium" color="text">
                  {x.caratula}
                </MDTypography>
              )}
            </MDBox>
            <MDBox ml="auto">
              {x.finish_date && (
                <MDBadge
                  badgeContent={x.finish_date && DateFormat(x.finish_date)}
                  color={x.finish_date ? "secondary" : null}
                  container
                ></MDBadge>
              )}
            </MDBox>
            <Icon
              sx={{ cursor: "pointer", fontWeight: "bold", ml: 1 }}
              fontSize="small"
              color="error"
              onClick={() => handleDelete(x.id)}
            >
              delete
            </Icon>
          </MDBox>
        </CardContent>
      </Card>
    </Grid>
  ));

    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} color="info" />
        </MDBox>
      );
    }

    return (
      <>
        {renderMenu}
        {renderModal}
        <Grid container spacing={2}>
          {listTasks}
          <Divider sx={{ my: 0.5 }} />
          {!showDone && (
            <Grid item xs={12} md={12} lg={12}>
              <MDButton variant="text" color="secondary" onClick={() => setShowDone(true)}>
                Ver tareas completadas
              </MDButton>
            </Grid>
          )}
          {showDone && (
            <>
              <Grid item xs={12} md={12} lg={12}>
                <MDButton variant="text" color="secondary" onClick={() => setShowDone(false)}>
                  Ocultar tareas completadas
                </MDButton>
              </Grid>
              {listTasksDone}
            </>
          )}
        </Grid>
      </>
    );
};

ListTask.propTypes = {
  refreshTasks: PropTypes.bool,
  onEdit: PropTypes.func,
};

export default ListTask;
